import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageSourcePropType, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, Path } from 'react-native-svg';

import { EventHeroBannerJpg } from '../../../assets/event';
import EventGradientText from './EventGradientText';
import EventPanel from './EventPanel';
import {
	EVENT_FONT,
	EVENT_FONT_CONDENSED,
	eventColors,
	HERO_FILL,
	HERO_TITLE_RAMP,
} from './eventTheme';

// Figma "Hero / World Cup / editable text + timer" (3324:18904): 360x148 on the content column.
export const HERO_DESIGN_WIDTH = 360;
export const HERO_DESIGN_HEIGHT = 148;
/** The brief's copy column. */
const COPY_WIDTH = 204;
/** Wider box for the title alone - see the note on `copy` in the stylesheet. */
const TITLE_WIDTH = 320;

type Props = {
	kicker: string;
	title: string;
	description: string;
	endsInLabel: string;
	/** Viewport width the 360-wide design should occupy. */
	width: number;
	/**
	 * Defaults to the banner exported from the brief. It is marked there as "ARTWORK PLACEHOLDER
	 * / replace fill with regenerated 777 banner", so pass the final art here when it lands.
	 */
	artwork?: ImageSourcePropType;
};

const ClockIcon = ({ size }: { size: number }) => (
	<Svg width={size} height={size} viewBox='0 0 16 16'>
		<Circle cx='8' cy='8' r='6.6' stroke={eventColors.gold} strokeWidth='1.3' fill='none' />
		<Path
			d='M8 4.4V8l2.6 1.6'
			stroke={eventColors.gold}
			strokeWidth='1.3'
			strokeLinecap='round'
			fill='none'
		/>
	</Svg>
);

export const EventHeroBanner = ({
	kicker,
	title,
	description,
	endsInLabel,
	width,
	artwork = EventHeroBannerJpg,
}: Props) => {
	const { t } = useTranslation();
	const scale = width / HERO_DESIGN_WIDTH;
	const styles = useMemo(() => createStyles(scale), [scale]);

	return (
		<EventPanel
			radius={12 * scale}
			borderWidth={Math.max(1, 1 * scale)}
			borderColor={eventColors.panelBorderGold}
			fill={HERO_FILL}
			style={styles.panel}
		>
			<Image source={artwork} style={styles.artwork} resizeMode='cover' />

			<View style={styles.copy}>
				<Text numberOfLines={1} style={[styles.copyLine, styles.kicker]}>
					{kicker}
				</Text>
				<EventGradientText
					text={title}
					stops={HERO_TITLE_RAMP}
					width={TITLE_WIDTH * scale}
					height={29 * scale}
					fontSize={26 * scale}
				/>
				<Text style={[styles.copyLine, styles.description]}>{description}</Text>
			</View>

			<View style={styles.timer}>
				<Text style={styles.timerLabel}>
					{t('T_SITE_EVENT_ENDS_IN', { defaultValue: 'ENDS IN:' })}
				</Text>
				<View style={styles.countdown}>
					<ClockIcon size={15 * scale} />
					<Text numberOfLines={1} style={styles.countdownLabel}>
						{endsInLabel}
					</Text>
				</View>
			</View>
		</EventPanel>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		panel: {
			width: HERO_DESIGN_WIDTH * scale,
			height: HERO_DESIGN_HEIGHT * scale,
		},
		// Explicit box, not absoluteFillObject: on Android an Image positioned only by the
		// absolute-fill shorthand paints at its intrinsic size instead of the box it is pinned to.
		artwork: {
			position: 'absolute',
			left: 0,
			top: 0,
			width: HERO_DESIGN_WIDTH * scale,
			height: HERO_DESIGN_HEIGHT * scale,
		},
		// Figma centres the copy on the banner and pins it 14 from the top, in a 204-wide column.
		// The title gets the wider box below: the design sets it in Sofia Sans Condensed and the
		// app only bundles Semi Condensed, which runs past 204 at 26px and clipped the last letters.
		copy: {
			position: 'absolute',
			left: (HERO_DESIGN_WIDTH / 2 - TITLE_WIDTH / 2) * scale,
			top: 14 * scale,
			width: TITLE_WIDTH * scale,
			alignItems: 'center',
			gap: 3 * scale,
		},
		copyLine: {
			width: COPY_WIDTH * scale,
		},
		// Figma "Label/Micro": Sofia Sans Condensed Black 12, letter-spacing 4 - kept wide here,
		// where the kicker is short enough to carry it.
		kicker: {
			color: eventColors.gold,
			fontFamily: EVENT_FONT_CONDENSED,
			fontSize: 15 * scale,
			fontWeight: '900',
			lineHeight: 17 * scale,
			textAlign: 'center',
		},
		description: {
			color: eventColors.white,
			fontFamily: EVENT_FONT,
			fontSize: 10 * scale,
			fontWeight: '700',
			lineHeight: 13 * scale,
			textAlign: 'center',
		},
		timer: {
			position: 'absolute',
			left: 0,
			right: 0,
			top: 102 * scale,
			alignItems: 'center',
			gap: 3 * scale,
		},
		timerLabel: {
			color: eventColors.accent,
			fontFamily: EVENT_FONT_CONDENSED,
			fontSize: 10 * scale,
			fontWeight: '900',
			lineHeight: 12 * scale,
		},
		countdown: {
			borderRadius: 8 * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: eventColors.countdownBorder,
			backgroundColor: eventColors.countdownPlate,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: 10 * scale,
			paddingVertical: 3 * scale,
			gap: 5 * scale,
		},
		countdownLabel: {
			color: eventColors.accent,
			fontFamily: EVENT_FONT,
			fontSize: 12 * scale,
			fontWeight: '700',
			lineHeight: 16 * scale,
		},
	});

export default EventHeroBanner;
